import React, { useContext } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageValidateSize from "filepond-plugin-image-validate-size";
import FilePondPluginImageCrop from "filepond-plugin-image-crop";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { enqueueSnackbar } from "notistack";
import { s2Url } from "@seabrookstudios/types";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { useQueryClient } from "@tanstack/react-query";
import { GameById, GameContracts, MyPublisherDocuments } from "@seabrookstudios/pitch2table";
import endpoints, { uploadContractToPublisher } from "../../endpoints";
import { useUploadImage } from "../../users/queries";

registerPlugin(FilePondPluginImagePreview);
registerPlugin(FilePondPluginImageValidateSize);
registerPlugin(FilePondPluginImageCrop);
registerPlugin(FilePondPluginFileValidateType);
registerPlugin(FilePondPluginImageResize);
registerPlugin(FilePondPluginImageTransform);
registerPlugin(FilePondPluginFileValidateSize);

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const UploadContractComponent = ({ gameId }) => {
  const token = useContext(CachedTokenContext);
  const queryClient = useQueryClient();

  const onFileUploadSuccess = () => {
    enqueueSnackbar("Latest contract uploaded", { variant: "success" });
    queryClient.invalidateQueries({ queryKey: GameContracts(gameId) });
    queryClient.invalidateQueries({ queryKey: GameById(gameId) });
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occured uploading the contract", { variant: "error" });
  };

  const { games } = endpoints();
  const url = games().contracts(gameId);
  const uploadImage = useUploadImage(s2Url(url), token, "contract", onFileUploadSuccess, onFileUploadFailure);

  return (
    <FilePond
      allowMultiple={false}
      allowReplace={false}
      allowRevert={false}
      allowProcess={false}
      credits={false}
      instantUpload={true}
      maxFiles={1}
      labelIdle={'Drag & Drop your contract or <span class="filepond--label-action"> Browse </span>'}
      maxFileSize="5MB"
      server={{
        process: uploadImage,
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const UploadContractToPublisherComponent = ({ publisherId }) => {
  const token = useContext(CachedTokenContext);
  const queryClient = useQueryClient();

  const onFileUploadSuccess = () => {
    enqueueSnackbar("Contract uploaded", { variant: "success" });
    queryClient.invalidateQueries({ queryKey: MyPublisherDocuments(publisherId) });
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading the contract", { variant: "error" });
  };

  const url = uploadContractToPublisher(publisherId);
  const uploadImage = useUploadImage(s2Url(url), token, "contract", onFileUploadSuccess, onFileUploadFailure);

  return (
    <FilePond
      allowMultiple={false}
      allowReplace={false}
      allowRevert={false}
      allowProcess={false}
      credits={false}
      instantUpload={true}
      maxFiles={1}
      labelIdle={'Drag & Drop your contract or <span class="filepond--label-action"> Browse </span>'}
      maxFileSize="5MB"
      server={{
        process: uploadImage,
      }}
    />
  );
};
